<script setup lang="ts">
import {
  useGetReportSectionsTree,
  useMoveSection,
  useCreateSection,
} from "@/stores/queries";
import {
  useSortReportImageToSection,
  useCreateReportImage,
} from "@/stores/queries/report-images.query";
import { useMergedReportImages } from "@/composable/useMergedReportImages";
import { DRTree } from "@/components/UI/tree";
import type { MoveEventPayload } from "@/components/UI/tree/types";
import { computed, ref, toRef } from "vue";
import { FINDINGS_PANEL_WIDTH } from "../../layout/constants";
import { useDragItem } from "../findings/useDragItem";
import PhotosSectionTreeItem from "./PhotosSectionTreeItem.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import DREditTextDialog from "@/components/UI/feedbacks/DREditTextDialog.vue";
import InsertSectionsFromTemplateModal from "./InsertSectionsFromTemplateModal.vue";
import SaveSectionsAsTemplateModal from "./SaveSectionsAsTemplateModal.vue";
import type { SectionItem } from "@/types/sections.types";

const props = defineProps<{
  diagnosticId: number;
  reportId: number;
  selectedSectionId?: number | null;
}>();

const emit = defineEmits<{
  (e: "select-section", sectionId: number | null): void;
  (e: "images-dropped"): void;
}>();

const diagnosticId = toRef(props, "diagnosticId");
const reportId = toRef(props, "reportId");

const handleSelectSection = (sectionId: number) => {
  // Toggle selection: if same section is clicked, deselect it
  if (props.selectedSectionId === sectionId) {
    emit("select-section", null);
  } else {
    emit("select-section", sectionId);
  }
};

const { data } = useGetReportSectionsTree(reportId);
// Use merged images for counting
const { mergedImages } = useMergedReportImages(diagnosticId, reportId);

const { mutateAsync: sortToSection, isPending: isSorting } =
  useSortReportImageToSection(reportId);
const { mutateAsync: createReportImage, isPending: isCreatingReportImage } =
  useCreateReportImage(reportId);
const { mutateAsync: moveSection, isPending: isMoving } = useMoveSection();
const { mutateAsync: createSection } = useCreateSection();

const { draggedItem } = useDragItem("image");

// Create first section dialog
const showCreateDialog = ref(false);
const showInsertTemplateModal = ref(false);
const showSaveTemplateModal = ref(false);

const handleCreateFirstSection = async (title: string) => {
  if (!title.trim()) return;
  await createSection({
    reportId: props.reportId,
    title: title.trim(),
    order: 0,
  });
};

const sections = computed<SectionItem[]>(() => data.value || []);

// Count images per section - now uses merged images
const imageCountBySection = computed(() => {
  const counts = new Map<number, number>();
  if (mergedImages.value) {
    mergedImages.value.forEach((img) => {
      if (img.sortedSectionId) {
        counts.set(
          img.sortedSectionId,
          (counts.get(img.sortedSectionId) || 0) + 1,
        );
      }
    });
  }
  return counts;
});

const getRecursiveImageCount = (section: SectionItem): number => {
  let count = imageCountBySection.value.get(section.id) || 0;
  if (section.children?.length) {
    for (const child of section.children) {
      count += getRecursiveImageCount(child);
    }
  }
  return count;
};

const getImageCount = (expanded: boolean, section: SectionItem): number => {
  return expanded
    ? imageCountBySection.value.get(section.id) || 0
    : getRecursiveImageCount(section);
};

const dragOverSectionId = ref<number | null>(null);

const isPending = computed(
  () => isSorting.value || isMoving.value || isCreatingReportImage.value,
);

// Handle tree section reordering
const handleMoveSection = (payload: MoveEventPayload) => {
  moveSection({
    id: payload.id,
    newParent: payload.parentId,
    newOrder: payload.order,
  });
};

// Image drop handling
const handleDragOver = (event: DragEvent, sectionId: number | null) => {
  event.preventDefault();
  dragOverSectionId.value = sectionId;
};

const handleDragLeave = () => {
  dragOverSectionId.value = null;
};

// Helper to ensure a reportImageId exists (create if virtual)
const ensureReportImageId = async (
  diagnosticImageId: number,
): Promise<number | null> => {
  const image = mergedImages.value?.find(
    (img) => img.diagnosticImageId === diagnosticImageId,
  );
  if (!image) return null;
  if (image.reportImageId) return image.reportImageId;
  if (image.isVirtual) {
    const newReportImage = await createReportImage({
      diagnosticImageId,
      reportId: props.reportId,
    });
    return newReportImage.id;
  }
  return null;
};

const handleDrop = async (event: DragEvent, sectionId: number | null) => {
  event.preventDefault();

  // Handle multiple selected images (receives diagnosticImageIds)
  const selectedIdsData = event.dataTransfer?.getData("selectedIds");
  if (selectedIdsData) {
    const selectedIds = JSON.parse(selectedIdsData) as number[];
    for (const diagnosticImageId of selectedIds) {
      const reportImageId = await ensureReportImageId(diagnosticImageId);
      if (reportImageId) {
        await sortToSection({ imageId: reportImageId, sectionId });
      }
    }
    emit("images-dropped");
  } else if (draggedItem.value && draggedItem.value.type === "image") {
    const diagnosticImageId = draggedItem.value.item.id;
    const reportImageId = await ensureReportImageId(diagnosticImageId);
    if (reportImageId) {
      await sortToSection({ imageId: reportImageId, sectionId });
    }
    emit("images-dropped");
  }

  dragOverSectionId.value = null;
};

// Get sibling count for a section
const getSiblingCount = (section: SectionItem): number => {
  if (!section.parentId) {
    return sections.value.length;
  }
  // Find parent and return its children count
  const findParent = (items: SectionItem[]): number => {
    for (const item of items) {
      if (item.id === section.parentId) {
        return item.children?.length ?? 0;
      }
      if (item.children?.length) {
        const result = findParent(item.children);
        if (result > 0) return result;
      }
    }
    return 0;
  };
  return findParent(sections.value);
};
</script>

<template>
  <div
    v-loading="isPending"
    class="sorting-panel bg-white border-start d-flex flex-column h-100"
    :style="`width: ${FINDINGS_PANEL_WIDTH}px; flex-shrink: 0`"
  >
    <!-- Header -->
    <div
      class="panel-header px-3 py-3 border-bottom d-flex align-items-center justify-content-between"
    >
      <div>
        <h6 class="mb-0 fw-medium">{{ $t("report:photos.sorting.title") }}</h6>
        <small class="text-muted">{{ $t("report:photos.sorting.hint") }}</small>
      </div>
      <el-dropdown trigger="click">
        <DRBtnIcon :size="28" icon="bi-three-dots" variant="flat" />
        <template #dropdown>
          <el-dropdown-menu>
            <DRDropdownItem
              icon="bi-download"
              :label="$t('report:photos.sections.templates.insertFromTemplate')"
              @select="showInsertTemplateModal = true"
            />
            <DRDropdownItem
              icon="bi-upload"
              :label="$t('report:photos.sections.templates.saveAsTemplate')"
              :disabled="!sections.length"
              @select="showSaveTemplateModal = true"
            />
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- Content -->
    <el-scrollbar class="flex-grow-1">
      <div class="p-3">
        <!-- Empty state -->
        <div
          v-if="!sections.length"
          class="empty-state d-flex flex-column align-items-center justify-content-center py-4 text-center"
        >
          <el-icon size="40" class="mb-2 text-muted">
            <i class="bi-folder-plus"></i>
          </el-icon>
          <p class="text-muted small mb-3">
            {{ $t("report:photos.sorting.noSections") }}
          </p>
          <div class="d-flex flex-column gap-2">
            <DRBtn
              icon="bi-plus-lg"
              :label="$t('report:photos.sections.addFirst')"
              variant="outline"
              size="sm"
              @click="showCreateDialog = true"
            />
            <DRBtn
              icon="bi-download"
              :label="$t('report:photos.sections.templates.insertFromTemplate')"
              variant="outline"
              size="sm"
              @click="showInsertTemplateModal = true"
            />
          </div>
        </div>

        <!-- Sections tree with DRTree -->
        <DRTree
          v-else
          :data="sections"
          :parent-id="null"
          :expandable="false"
          @move="handleMoveSection"
          v-slot="{ item, expanded }"
        >
          <!-- Wrap each section with a drop zone for images -->
          <div
            class="section-drop-wrapper"
            :class="{ 'drop-active': dragOverSectionId === item.id }"
            @dragover.prevent="handleDragOver($event, item.id)"
            @dragleave="handleDragLeave"
            @drop.prevent="handleDrop($event, item.id)"
          >
            <PhotosSectionTreeItem
              :section="item"
              :report-id="reportId"
              :sibling-count="getSiblingCount(item)"
              :image-count="getImageCount(expanded, item)"
              :selected="selectedSectionId === item.id"
              @select="handleSelectSection"
            />
          </div>
        </DRTree>
      </div>
    </el-scrollbar>
  </div>

  <!-- Create first section dialog -->
  <DREditTextDialog
    v-model="showCreateDialog"
    :title="$t('report:photos.sections.addFirst')"
    :placeholder="$t('report:photos.sections.newSectionName')"
    @confirm="handleCreateFirstSection"
  />

  <!-- Insert sections from template -->
  <InsertSectionsFromTemplateModal
    v-model="showInsertTemplateModal"
    :report-id="reportId"
    @inserted="showInsertTemplateModal = false"
  />

  <!-- Save sections as template -->
  <SaveSectionsAsTemplateModal
    v-model="showSaveTemplateModal"
    :report-id="reportId"
    @saved="showSaveTemplateModal = false"
  />
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

.sorting-panel {
  z-index: 1;
}

.panel-header {
  flex-shrink: 0;
}

.drop-zone {
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px dashed transparent;
  transition: all 0.15s ease;
  cursor: default;

  &:hover {
    background-color: var(--bs-light);
  }

  &.drop-active {
    background-color: rgba($primary, 0.08);
    border-color: $primary;
  }
}

.unclassified-zone {
  background-color: var(--bs-light);
}

.section-drop-wrapper {
  width: 100%;
  min-width: 0;
  overflow: hidden;
  border-radius: 4px;
  border: 1px dashed transparent;
  transition: all 0.15s ease;

  &.drop-active {
    background-color: rgba($primary, 0.08);
    border-color: $primary;
  }
}

.empty-state {
  color: #9ca3af;
}

:deep(.tree-item-row) {
  padding: 0;
  min-width: 0;
  overflow: hidden;
}

:deep(.tree-item) {
  min-width: 0;
  overflow: hidden;
}
</style>
